/* eslint-disable no-console */
import { chunk } from "lodash"
import { firestore as db } from "src/Firebase"
import { serverTimestamp } from "firebase/firestore"
import { toDecimal } from "src/utils/toDecimal"

export const getUserByEmail = email =>
  db.collection("usuarios").where("Correo", "==", email).get()

export const getUserByDNI = async dni => {
  const doc = await db
    .collection("usuarios")
    .where("Cedula", "==", dni?.trim().toUpperCase())
    .limit(1)
    .get()

  return doc.empty ? null : doc.docs[0].data()
}

export const newUser = (uid, user) => {
  if (uid) {
    return db.collection("usuarios").doc(uid).set(user)
  }
  return db.collection("usuarios").add(user)
}

export const updateUser = (data, documentoId) =>
  db.collection("usuarios").doc(documentoId).update(data)

export const updateUserActive = (data, documentoId) =>
  db.collection("usuarios").doc(documentoId).set(data, { merge: true })

export const updateRelatedLists = async (userId, conjuntoId, isActive) => {
  try {
    // Referencia a la colección donde están los documentos relacionados
    const listaRef = db.collection(`conjuntos/${conjuntoId}/lista`)

    // Buscar documentos donde el campo Lugar contiene el ID del usuario
    const snapshot = await listaRef.get().then(querySnapshot =>
      querySnapshot.docs.filter(doc => {
        const lugar = doc.data().Lugar
        const lugarParts = lugar.split("/")
        return lugarParts[1] === userId
      })
    )
    // Actualizar el campo Activo de los documentos encontrados
    const batch = db.batch()
    snapshot.forEach(doc => {
      const data = doc.data()
      const updateData = { Activo: isActive }

      // Si el campo Activo no existe, lo agregamos
      if (!("Activo" in data)) {
        updateData.Activo = isActive
      }

      batch.update(doc.ref, updateData)
    })

    // Confirmar los cambios en la base de datos
    await batch.commit()
  } catch (error) {
    console.error("Error actualizando documentos relacionados:", error)
  }
}

export const updateRelatedPlates = async (userId, conjuntoId, status) => {
  try {
    // Referencia a la colección donde están los documentos relacionados
    const placasRef = db.collection(`conjuntos/${conjuntoId}/placas`)

    // Buscar documentos donde el campo idresidente coincide con el userId
    const snapshot = await placasRef.where("idresidente", "==", userId).get()
    if (snapshot.empty) {
      console.warn("No se encontraron documentos para actualizar.")
      return []
    }

    const docsToUpdate = snapshot.docs.filter(doc => {
      const currentStatus = `${doc.data()?.estado || ""}`.toLowerCase()
      return currentStatus !== "inactivo"
    })

    if (docsToUpdate.length === 0) {
      return []
    }

    // Crear un batch para actualizaciones masivas
    const batch = db.batch()

    docsToUpdate.forEach(doc => {
      batch.update(doc.ref, { estado: status, updated_at: serverTimestamp() })
    })

    // Confirmar los cambios en la base de datos
    await batch.commit()
    return docsToUpdate.map(doc => doc.data())
  } catch (error) {
    console.error("Error actualizando documentos relacionados:", error)
    return []
  }
}

export const updateRelatedTags = async (userId, conjuntoId, status) => {
  try {
    const tagsRef = db.collection(`conjuntos/${conjuntoId}/tags`)
    const snapshot = await tagsRef.where("idresidente", "==", userId).get()
    if (snapshot.empty) {
      console.warn("No se encontraron documentos para actualizar.")
      return
    }
    const batch = db.batch()
    snapshot.forEach(doc => {
      batch.update(doc.ref, { estado: status, updated_at: serverTimestamp() })
    })
    await batch.commit()
  } catch (error) {
    console.error("Error actualizando documentos relacionados:", error)
  }
}

export const updateRelatedFaces = async (
  userId,
  identificacion,
  conjuntoId,
  status
) => {
  try {
    const normalizedStatus = `${status || ""}`.toLowerCase()
    const isBlocked = normalizedStatus !== "activo"
    const unidadesRef = db.collection(
      `conjuntos/${conjuntoId}/unidadesHabitacionales`
    )
    const [propietarioSnapshot, inquilinoSnapshot, biometricosSnapshot] =
      await Promise.all([
        unidadesRef.where("IdPropietario", "==", userId).get(),
        unidadesRef.where("IdInquilino", "==", userId).get(),
        db.collection(`conjuntos/${conjuntoId}/rostros`).get(),
      ])
    if (biometricosSnapshot.empty) {
      console.warn("No se encontraron biometricos para actualizar rostros.")
      return
    }
    const destinos = [
      ...new Set(
        [...propietarioSnapshot.docs, ...inquilinoSnapshot.docs]
          .map(doc => doc.data()?.Numero)
          .filter(Boolean)
      ),
    ]
    const queryPromises = []
    biometricosSnapshot.docs.forEach(biometricoDoc => {
      const imagenesRef = biometricoDoc.ref.collection("imagenes")
      if (identificacion) {
        queryPromises.push(
          imagenesRef.where("identificacion", "==", identificacion).get()
        )
      }
      if (destinos.length > 0) {
        queryPromises.push(imagenesRef.where("destino", "in", destinos).get())
      }
    })
    if (queryPromises.length === 0) {
      console.warn("No se encontraron criterios para actualizar rostros.")
      return
    }

    const snapshots = await Promise.all(queryPromises)
    const docsResult = new Map()
    snapshots.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        docsResult.set(doc.ref.path, doc)
      })
    })

    const imagenesDocs = [...docsResult.values()]
    if (imagenesDocs.length === 0) {
      console.warn("No se encontraron rostros para actualizar.")
      return
    }
    const batch = db.batch()
    imagenesDocs.forEach(imagenDoc => {
      batch.update(imagenDoc.ref, {
        estado: status,
        is_sync: false,
        is_sync_blocked: isBlocked,
      })
    })

    await batch.commit()
  } catch (error) {
    console.error("Error actualizando rostros relacionados:", error)
  }
}

const formatUnit = value => `${value ?? ""}`.trim()

const getDocsByProperty = async (ref, numeroCasa) => {
  const casaAsString = `${numeroCasa ?? ""}`
  const [snapshotRaw, snapshotString] = await Promise.all([
    ref.where("destino", "==", numeroCasa).get(),
    ref.where("destino", "==", casaAsString).get(),
  ])

  const docsMap = new Map()
  ;[...snapshotRaw.docs, ...snapshotString.docs].forEach(doc => {
    docsMap.set(doc.ref.path, doc)
  })

  return [...docsMap.values()]
}

export const updateRelatedPlatesByProperty = async (
  conjuntoId,
  numeroCasa,
  status,
  nextValue = null
) => {
  try {
    const placasRef = db.collection(`conjuntos/${conjuntoId}/placas`)
    const docs = await getDocsByProperty(placasRef, numeroCasa)
    if (docs.length === 0) {
      console.warn("No se encontraron placas para actualizar por propiedad.")
      return []
    }

    const docsToUpdate = docs.filter(doc => {
      const currentStatus = `${doc.data()?.estado || ""}`.toLowerCase()
      return currentStatus !== "inactivo"
    })

    if (docsToUpdate.length === 0) {
      return []
    }

    const batch = db.batch()
    docsToUpdate.forEach(doc => {
      const data = {
        estado: status,
        updated_at: serverTimestamp(),
      }

      if (nextValue !== undefined && nextValue !== null) {
        const normalized = formatUnit(nextValue)
        if (normalized !== "") {
          data.destino = normalized
        }
      }

      batch.update(doc.ref, data)
    })
    await batch.commit()
    return docsToUpdate.map(doc => doc.data())
  } catch (error) {
    console.error("Error actualizando placas por propiedad:", error)
    return []
  }
}

export const updateRelatedTagsByProperty = async (
  conjuntoId,
  numeroCasa,
  status,
  nextValue = null
) => {
  try {
    const tagsRef = db.collection(`conjuntos/${conjuntoId}/tags`)
    const docs = await getDocsByProperty(tagsRef, numeroCasa)
    if (docs.length === 0) {
      console.warn("No se encontraron tags para actualizar por propiedad.")
      return
    }

    const batch = db.batch()
    docs.forEach(doc => {
      const data = {
        estado: status,
        updated_at: serverTimestamp(),
      }

      if (nextValue !== undefined && nextValue !== null) {
        const normalized = formatUnit(nextValue)
        if (normalized !== "") {
          data.destino = normalized
        }
      }

      batch.update(doc.ref, data)
    })
    await batch.commit()
  } catch (error) {
    console.error("Error actualizando tags por propiedad:", error)
  }
}

export const deleteAccessbyUnit = async (conjuntoId, numeroCasa) => {
  try {
    const [placasDocs, tagsDocs, facesDocs] = await Promise.all([
      getDocsByProperty(
        db.collection(`conjuntos/${conjuntoId}/placas`),
        numeroCasa
      ),
      getDocsByProperty(
        db.collection(`conjuntos/${conjuntoId}/tags`),
        numeroCasa
      ),
      (async () => {
        const biometricosSnapshot = await db
          .collection(`conjuntos/${conjuntoId}/rostros`)
          .get()

        if (biometricosSnapshot.empty) {
          return []
        }

        const snapshots = await Promise.all(
          biometricosSnapshot.docs.map(async biometricoDoc => {
            const imagenesRef = biometricoDoc.ref.collection("imagenes")
            return Promise.all([
              imagenesRef.where("destino", "==", numeroCasa).get(),
              imagenesRef.where("destino", "==", `${numeroCasa ?? ""}`).get(),
            ])
          })
        )

        const docsMap = new Map()
        snapshots.flat().forEach(snapshot => {
          snapshot.docs.forEach(doc => {
            docsMap.set(doc.ref.path, doc)
          })
        })

        return [...docsMap.values()]
      })(),
    ])

    const hasRelatedUpdates =
      placasDocs.length > 0 || tagsDocs.length > 0 || facesDocs.length > 0

    if (!hasRelatedUpdates) {
      return { placas: 0, tags: 0, rostros: 0 }
    }

    const batch = db.batch()

    placasDocs.forEach(doc => {
      batch.update(doc.ref, {
        estado: "Inactivo",
        destino: "",
        updated_at: serverTimestamp(),
      })
    })

    tagsDocs.forEach(doc => {
      batch.update(doc.ref, {
        estado: "Inactivo",
        destino: "",
        updated_at: serverTimestamp(),
      })
    })

    facesDocs.forEach(doc => {
      batch.update(doc.ref, {
        estado: "inactivo",
        destino: "",
        is_sync: false,
        is_sync_blocked: true,
      })
    })

    await batch.commit()

    return {
      placas: placasDocs.length,
      tags: tagsDocs.length,
      rostros: facesDocs.length,
    }
  } catch (error) {
    console.error("Error eliminando registros asociados:", error)
    return { placas: 0, tags: 0, rostros: 0 }
  }
}

export const updateRelatedFacesByProperty = async (
  conjuntoId,
  numeroCasa,
  status,
  nextValue = null
) => {
  try {
    const normalizedStatus = `${status || ""}`.toLowerCase()
    const isBlocked = normalizedStatus !== "activo"
    const biometricosSnapshot = await db
      .collection(`conjuntos/${conjuntoId}/rostros`)
      .get()

    if (biometricosSnapshot.empty) {
      console.warn("No se encontraron biometricos para actualizar rostros.")
      return []
    }

    const queryPromises = []
    biometricosSnapshot.docs.forEach(biometricoDoc => {
      const imagenesRef = biometricoDoc.ref.collection("imagenes")
      queryPromises.push(imagenesRef.where("destino", "==", numeroCasa).get())
      queryPromises.push(
        imagenesRef.where("destino", "==", `${numeroCasa ?? ""}`).get()
      )
    })

    const snapshots = await Promise.all(queryPromises)
    const docsMap = new Map()
    snapshots.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        docsMap.set(doc.ref.path, doc)
      })
    })

    const docs = [...docsMap.values()]
    if (docs.length === 0) {
      console.warn("No se encontraron rostros para actualizar por propiedad.")
      return []
    }
    const batch = db.batch()
    docs.forEach(doc => {
      const update = {
        estado: status,
        is_sync: false,
        is_sync_blocked: isBlocked,
      }

      if (nextValue !== undefined && nextValue !== null) {
        const normalized = formatUnit(nextValue)
        if (normalized !== "") {
          update.destino = normalized
        }
      }

      batch.update(doc.ref, update)
    })
    await batch.commit()
    const identificaciones = [
      ...new Set(docs.map(doc => doc.data().identificacion).filter(Boolean)),
    ]
    return identificaciones
  } catch (error) {
    console.error("Error actualizando rostros por propiedad:", error)
    return []
  }
}

export const updateListas = (conjuntoId, documentoId, data) =>
  db
    .collection("conjuntos")
    .doc(conjuntoId)
    .collection("lista")
    .doc(documentoId)
    .update(data)

export const updateUserFamiliares = (documentoId, data) =>
  db.collection("usuarios").doc(documentoId).update({
    Familia: data,
  })

export const updatecuentasPorCobrar = (data, conjuntoId, cuentasId) =>
  db
    .collection("conjuntos")
    .doc(conjuntoId)
    .collection("cuentasPorCobrar")
    .doc(cuentasId)
    .update({
      CasaUsuario: data.Casa,
      NombreUsuario: `${data.Nombre} ${data.Apellido}`,
    })

export const deleteUserbyID = userId =>
  db.collection("usuarios").doc(userId).delete()

export const deleteUsersBulk = async (listOfUsers = []) => {
  console.log(`Start to delete ${listOfUsers.length} users`)

  const chunckedList = chunk(listOfUsers, 500)
  const deletePromises = chunckedList.map(part => {
    const batch = db.batch()
    part.forEach(user => {
      const deleteRef = db.collection("usuarios").doc(user.id)
      batch.delete(deleteRef)
    })
    return batch.commit()
  })
  await Promise.allSettled(deletePromises)
  console.log(`Delete ${listOfUsers.length} users`)
}

export const getDeleteUsersBulkPassword = async () => {
  const snapshot = await db
    .collection("configuracion")
    .doc("DeleteUsersBulk")
    .get()

  return snapshot.data()?.password || ""
}

export const getConjunto = conjuntoId =>
  db.collection("conjuntos").doc(conjuntoId).get()

export const getUserByConjunto = (observer, conjuntoID) =>
  db
    .collection("usuarios")
    .where("ConjuntoUidResidencia", "==", conjuntoID)
    .onSnapshot(observer)

export const validateEmailExists = (mail, conjuntoId) =>
  db
    .collection("usuarios")
    .where("ConjuntoUidResidencia", "==", conjuntoId)
    .where("Correo", "==", mail)
    .limit(1)
    .get()

export const validateDNIExists = (dni, conjuntoId) =>
  db
    .collection("usuarios")
    .where("ConjuntoUidResidencia", "==", conjuntoId)
    .where("Cedula", "==", dni)
    .limit(1)
    .get()

export const getHousingUnits = (conjuntoId, observer) =>
  db
    .collection("conjuntos")
    .doc(conjuntoId)
    .collection("unidadesHabitacionales")
    .onSnapshot(observer)

export const getListas = (conjuntoId, observer) =>
  db
    .collection("conjuntos")
    .doc(conjuntoId)
    .collection("lista")
    .onSnapshot(observer)

export const getCuentasCobrar = (conjuntoId, casa) =>
  db
    .collection("conjuntos")
    .doc(conjuntoId)
    .collection("cuentasPorCobrar")
    .where("CasaUsuario", "==", casa)
    .get()

export const updateHousingUnit = (conjuntoId, unitId, data) =>
  db
    .collection("conjuntos")
    .doc(conjuntoId)
    .collection("unidadesHabitacionales")
    .doc(unitId)
    .update(data)

export const newPagoPendiente = (conjuntoId, data) =>
  db
    .collection("conjuntos")
    .doc(conjuntoId)
    .collection("cuentasPorCobrar")
    .add({
      Nombre: data.Nombre,
      Valor: toDecimal(data.Valor * 1),
      FechaLimite: new Date(data.FechaLimite),
      FechaRegistro: new Date(),
      Descripcion: data.Descripcion,
      UsuarioId: data.UsuarioId,
      NombreUsuario: data.NombreUsuario,
      CasaUsuario: data.CasaUsuario,
      Rubro: data.Rubro,
      SubRubro: data.SubRubro,
      ValorOriginal: toDecimal(data.Valor * 1),
    })

export const updateUnidadHabitacional = (conjuntoUid, unidad, data) =>
  db
    .collection("conjuntos")
    .doc(conjuntoUid)
    .collection("unidadesHabitacionales")
    .doc(unidad)
    .update({
      UltimaExpensa: new Date(data),
      FechaUltimoPago: new Date(data),
    })

export const getDataProtec = async users => {
  const refs = users.map(user =>
    db
      .collection("usuarios")
      .doc(user.id)
      .collection("AcuerdoLegal")
      .doc("data")
  )
  const data = await Promise.all(refs.map(ref => ref.get()))
  return data.map((doc, idx) => {
    const docData = doc.data()
    return {
      ...users[idx],
      acepta: docData ? docData.AceptaAcuerdoLegal : false,
      fecha: docData ? new Date(docData.fecha) : null,
    }
  })
}
